/**
 * Tab title and favicon signalling. The tab strip is the only place a rep can see which
 * of several open tabs holds the live dialer, so state has to reach it.
 *
 * Claims are keyed and prioritised because the page has more than one thing worth
 * signalling and only one title: an AI stream and a ringing call can be true at once,
 * and without an arbiter each writer would overwrite the other's title and favicon.
 */

export type AiActivityState = 'generating' | 'complete' | 'error';
export type DialerActivityState = 'dialer-ready' | 'dialer-ringing' | 'dialer-in-call';
export type TabActivityState = AiActivityState | DialerActivityState;

/**
 * Which claim wins while several are held. A ringing call outranks everything because it
 * expires if the rep misses it; the ambient "the dialer lives in this tab" marker outranks
 * nothing, so it shows only when the tab has nothing more urgent to say.
 */
const Priority: Record<TabActivityState, number> = {
	'dialer-ringing': 60,
	'dialer-in-call': 50,
	generating: 40,
	error: 30,
	complete: 20,
	'dialer-ready': 10,
};

/** No prefix means favicon only — an ambient marker should not rewrite the tab's title. */
const TitlePrefixes: Partial<Record<TabActivityState, string>> = {
	complete: '✅',
	error: '⚠️',
	generating: '⏳',
	'dialer-ringing': '📞',
	'dialer-in-call': '🔴',
};

// Canvas fillStyle cannot resolve var() strings, so THEME.colors tokens are unusable here directly.
// Resolve the same CSS variables THEME points at; the fallbacks mirror palette.css tokens
// --palette-trickster-green-m, --palette-spell-red-m, --palette-magic-orange-m and
// --palette-levitate-blue-m respectively.
const BadgeColors: Record<TabActivityState, { cssVariable: string; fallback: string }> = {
	complete: { cssVariable: '--color-success-main', fallback: '#99db56' },
	error: { cssVariable: '--color-danger-main', fallback: '#de2139' },
	generating: { cssVariable: '--color-warning-main', fallback: '#ff9d52' },
	'dialer-ready': { cssVariable: '--color-primary-main', fallback: '#0066ff' },
	'dialer-ringing': { cssVariable: '--color-danger-main', fallback: '#de2139' },
	'dialer-in-call': { cssVariable: '--color-success-main', fallback: '#99db56' },
};

/** Key the AI streaming hook claims under, so the dialer's claim is a separate entry. */
const AI_CLAIM = 'ai-activity';

/** A steady badge is easy to miss in a strip of tabs; a ringing call has to pulse. */
const BLINK_INTERVAL_MS = 700;

const claims = new Map<string, TabActivityState>();

let baseTitle: string | null = null;
let lastAppliedTitle: string | null = null;
let originalFaviconHrefs: Map<HTMLLinkElement, string> | null = null;
// Badging is async (image loads); the token discards stale draws when the state changes mid-flight.
let renderToken = 0;
let blinkTimer: ReturnType<typeof setInterval> | null = null;
let blinkVisible = true;

function resolveBadgeColor(state: TabActivityState) {
	const { cssVariable, fallback } = BadgeColors[state];
	const resolved = window.getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim();
	return resolved || fallback;
}

function drawBadgedIcon(href: string, color: string): Promise<string | null> {
	return new Promise(resolve => {
		const image = new Image();
		image.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = image.naturalWidth;
				canvas.height = image.naturalHeight;
				// SVG icons without intrinsic dimensions report 0x0; badging one would blank the favicon.
				if (canvas.width === 0 || canvas.height === 0) {
					resolve(null);
					return;
				}
				const context = canvas.getContext('2d');
				if (!context) {
					resolve(null);
					return;
				}
				context.drawImage(image, 0, 0);
				const radius = canvas.width * 0.26;
				const center = { x: canvas.width - radius, y: canvas.height - radius };
				context.beginPath();
				context.arc(center.x, center.y, radius, 0, Math.PI * 2);
				context.fillStyle = color;
				context.fill();
				context.lineWidth = Math.max(1, radius * 0.25);
				context.strokeStyle = '#ffffff';
				context.stroke();
				resolve(canvas.toDataURL('image/png'));
			} catch {
				// A tainted canvas (cross-origin favicon) throws on toDataURL; skip the badge.
				resolve(null);
			}
		};
		image.onerror = () => resolve(null);
		image.src = href;
	});
}

function getFaviconLinks(): HTMLLinkElement[] {
	// rel~="icon" matches both rel="icon" and rel="shortcut icon"
	return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
}

function captureOriginalFavicons() {
	if (!originalFaviconHrefs) {
		originalFaviconHrefs = new Map(getFaviconLinks().map(link => [link, link.href]));
	}
	return originalFaviconHrefs;
}

/**
 * Restores via a live query rather than iterating the map, so we never write to detached
 * nodes if something else replaced the `<link>` elements while a badge was active.
 */
function restoreFavicons() {
	getFaviconLinks().forEach(link => {
		const originalHref = originalFaviconHrefs?.get(link);
		if (originalHref !== undefined) {
			link.href = originalHref;
		}
	});
}

async function applyFaviconBadges(state: TabActivityState, token: number) {
	const originals = captureOriginalFavicons();
	const color = resolveBadgeColor(state);
	await Promise.all(
		getFaviconLinks().map(async link => {
			const originalHref = originals.get(link);
			if (!originalHref) {
				return;
			}
			const badgedHref = await drawBadgedIcon(originalHref, color);
			if (badgedHref && token === renderToken) {
				link.href = badgedHref;
			}
		})
	);
}

function paintFavicon(state: TabActivityState) {
	renderToken++;
	if (blinkVisible) {
		void applyFaviconBadges(state, renderToken);
	} else {
		restoreFavicons();
	}
}

function stopBlinking() {
	if (blinkTimer) {
		clearInterval(blinkTimer);
		blinkTimer = null;
	}
	blinkVisible = true;
}

function startBlinking(state: TabActivityState) {
	// Already pulsing: leave the timer alone so the phase does not restart on every render.
	if (blinkTimer) return;
	blinkTimer = setInterval(() => {
		blinkVisible = !blinkVisible;
		paintFavicon(state);
	}, BLINK_INTERVAL_MS);
}

function applyTitle(prefix: string | undefined) {
	if (prefix === undefined) {
		// Undo a prefix we applied, but never a title someone else has since written.
		if (lastAppliedTitle !== null && baseTitle !== null && document.title === lastAppliedTitle) {
			document.title = baseTitle;
		}
		lastAppliedTitle = null;
		return;
	}
	// Re-capture the base title if something else (e.g. account load) has changed it since we last wrote it.
	if (baseTitle === null || document.title !== lastAppliedTitle) {
		baseTitle = document.title;
	}
	lastAppliedTitle = `${prefix} ${baseTitle}`;
	document.title = lastAppliedTitle;
}

function highestPriorityClaim(): TabActivityState | null {
	let winner: TabActivityState | null = null;
	claims.forEach(state => {
		if (winner === null || Priority[state] > Priority[winner]) {
			winner = state;
		}
	});
	return winner;
}

function render() {
	const state = highestPriorityClaim();

	if (state === null) {
		renderToken++;
		stopBlinking();
		applyTitle(undefined);
		restoreFavicons();
		baseTitle = null;
		originalFaviconHrefs = null;
		return;
	}

	if (state === 'dialer-ringing') {
		startBlinking(state);
	} else {
		stopBlinking();
	}

	applyTitle(TitlePrefixes[state]);
	// Through the painter, so a claim changing mid-pulse cannot relight the badge and leave
	// it on across two phases — repeated renders would otherwise flatten the pulse entirely.
	paintFavicon(state);
}

/**
 * Hold, change, or drop (`null`) one keyed claim on the tab's title and favicon. Callers
 * own their key and must release it on teardown, or the tab keeps signalling their state.
 */
export function claimTabIndicator(key: string, state: TabActivityState | null): void {
	if (state === null) {
		claims.delete(key);
	} else {
		claims.set(key, state);
	}
	render();
}

export function showTabActivityIndicator(state: AiActivityState) {
	claimTabIndicator(AI_CLAIM, state);
}

export function isTabActivityIndicatorShown() {
	return claims.has(AI_CLAIM);
}

export function clearTabActivityIndicator() {
	claimTabIndicator(AI_CLAIM, null);
}
